import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { TbChevronDown, TbSearch } from "react-icons/tb";
import { CurrencyIcon } from "../../../../Components/Atoms/CurrencyIcon";
import {
  PORTAL_DROPDOWN_BACKDROP_Z_INDEX,
  PORTAL_DROPDOWN_Z_INDEX,
  usePortalDropdownPosition,
} from "../../../../Hooks/usePortalDropdownPosition";
import { CURRENCIES, getCurrency } from "../constants";

const TRIGGER_CLASS =
  "valut-input flex h-11 w-full min-w-0 items-center justify-between gap-2 rounded-xl !pr-10 text-left text-sm";

const SEARCH_THRESHOLD = 6;

const formatOptionLabel = (currency) => (
  <>
    <CurrencyIcon asset={currency.id} />
    <span className="min-w-0 truncate text-brand-dark">
      <span className="font-semibold">{currency.code}</span>
      <span className="text-gray-500"> — {currency.label}</span>
    </span>
  </>
);

export const CurrencySelect = ({
  label,
  value,
  onChange,
  options = CURRENCIES,
  exclude,
  placeholder = "Selecione a moeda",
  id,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { triggerRef, menuRef, menuStyle } = usePortalDropdownPosition(open);

  const availableOptions = useMemo(
    () => options.filter((currency) => currency.id !== exclude),
    [options, exclude]
  );

  const selected =
    availableOptions.find((currency) => currency.id === value) ||
    getCurrency(value);

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return availableOptions;
    return availableOptions.filter(
      (currency) =>
        currency.code.toLowerCase().includes(term) ||
        currency.label.toLowerCase().includes(term) ||
        currency.id.toLowerCase().includes(term)
    );
  }, [availableOptions, search]);

  const showSearch = availableOptions.length >= SEARCH_THRESHOLD;

  const handleSelect = (currencyId) => {
    onChange(currencyId);
    setOpen(false);
    setSearch("");
  };

  const handleClose = () => {
    setOpen(false);
    setSearch("");
  };

  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className="mb-2 block text-xs font-medium text-gray-500">
          {label}
        </label>
      )}

      <div className="relative">
        <button
          ref={triggerRef}
          id={id}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
          className={`${TRIGGER_CLASS} ${selected ? "text-brand-dark" : "text-gray-400"}`}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2">
            {selected ? formatOptionLabel(selected) : placeholder}
          </span>
        </button>
        <TbChevronDown
          className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />

        {open &&
          menuStyle &&
          createPortal(
            <>
              <div
                className="fixed inset-0"
                style={{ zIndex: PORTAL_DROPDOWN_BACKDROP_Z_INDEX }}
                onClick={handleClose}
                aria-hidden="true"
              />
              <div
                ref={menuRef}
                role="listbox"
                style={{
                  ...menuStyle,
                  zIndex: PORTAL_DROPDOWN_Z_INDEX,
                  display: "flex",
                  flexDirection: "column",
                }}
                className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-card"
              >
                {showSearch && (
                  <div className="shrink-0 border-b border-gray-100 p-2">
                    <div className="relative">
                      <TbSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar moeda..."
                        className="valut-input h-9 w-full rounded-lg pl-9 pr-3 text-sm"
                        autoFocus
                      />
                    </div>
                  </div>
                )}

                <div className="min-h-0 flex-1 overflow-y-auto py-1">
                  {filteredOptions.length === 0 ? (
                    <p className="px-3 py-4 text-center text-xs text-gray-400">
                      Nenhuma moeda encontrada.
                    </p>
                  ) : (
                    filteredOptions.map((currency) => {
                      const isActive = currency.id === value;
                      return (
                        <button
                          key={currency.id}
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          onClick={() => handleSelect(currency.id)}
                          className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${
                            isActive
                              ? "bg-[#fff8ec] text-brand-dark"
                              : "text-brand-dark hover:bg-gray-50"
                          }`}
                        >
                          {formatOptionLabel(currency)}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </>,
            document.body
          )}
      </div>
    </div>
  );
};
